import type { IBimRelationshipEdge, IBimRelationshipNode } from '@skystone/shared';
import type { IGraphLayout } from '@/lib/bim-graph-layout';
import type { IRoutedEdge } from '@/lib/bim-edge-routing';
import { BimEdge } from './BimEdge';
import { BimTableCard } from './BimTableCard';

export interface BimGraphCanvasProps {
  layout: IGraphLayout;
  nodes: IBimRelationshipNode[];
  edges: IBimRelationshipEdge[];
  routedEdges: Map<string, IRoutedEdge>;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  dimmedNodeIds: Set<string>;
  dimmedEdgeIds: Set<string>;
  highlightedColumnsByNode: Map<string, Set<string>>;
  showLabels: boolean;
  onSelectNode: (nodeId: string) => void;
  onSelectEdge: (edgeId: string) => void;
}

const EMPTY_COLUMNS: Set<string> = new Set();

export function BimGraphCanvas(props: BimGraphCanvasProps) {
  const {
    layout,
    nodes,
    edges,
    routedEdges,
    selectedNodeId,
    selectedEdgeId,
    dimmedNodeIds,
    dimmedEdgeIds,
    highlightedColumnsByNode,
    showLabels,
    onSelectNode,
    onSelectEdge,
  } = props;

  return (
    <svg
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      preserveAspectRatio="xMidYMid meet"
      className="h-full w-full"
      role="img"
      aria-label="BIM table relationship diagram"
    >
      <g>
        {edges.map((edge) => {
          const routed = routedEdges.get(edge.id);
          if (!routed) {
            return null;
          }

          return (
            <BimEdge
              key={edge.id}
              edge={edge}
              routed={routed}
              isSelected={selectedEdgeId === edge.id}
              isDimmed={dimmedEdgeIds.has(edge.id)}
              showLabel={showLabels}
              onSelect={onSelectEdge}
            />
          );
        })}
      </g>

      <g>
        {nodes.map((node) => {
          const card = layout.cards.get(node.id);
          if (!card) {
            return null;
          }

          return (
            <foreignObject key={node.id} x={card.x} y={card.y} width={card.width} height={card.height}>
              <BimTableCard
                node={node}
                isSelected={selectedNodeId === node.id}
                isDimmed={dimmedNodeIds.has(node.id)}
                highlightedColumns={highlightedColumnsByNode.get(node.id) ?? EMPTY_COLUMNS}
                onSelect={onSelectNode}
              />
            </foreignObject>
          );
        })}
      </g>
    </svg>
  );
}
